import React, { useMemo } from 'react';
import styled from 'styled-components';
import { Avatar, Button, ButtonProps } from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import { CameraIcon } from '../Icons';
import { generateAvatarColor } from '@src/helpers/AvatarHelper';

const ImageBackdrop = styled.span`
	position: absolute;
	left: 0;
	right: 0;
	top: 0;
	bottom: 0;
	background-color: #000;
	opacity: 0;
	transition: opacity 0.4s;
	display: none;
`;

const Image = styled.span`
	position: absolute;
	left: 0;
	right: 0;
	top: 0;
	bottom: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	opacity: 0;
	color: #fff;
`;

const StyledAvatar = styled(Avatar)<{ image?: string; bgColor?: string }>`
	height: 150px;
	width: 150px;
	cursor: pointer;
	background-color: ${(props) => props.bgColor || 'transparent'};
	background-size: cover;
	background-position: center;
	position: relative;

	& .MuiImageBackdrop-root {
		opacity: 0;
		transition: opacity 0.4s;
		display: none;
		position: absolute;
		left: 0;
		right: 0;
		top: 0;
		bottom: 0;
		background-color: #000;
	}

	&:hover .MuiImageBackdrop-root {
		opacity: 0.4;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	&:hover ${Image} {
		opacity: ${(props) => (props.image ? '0.8' : '1')};
	}
`;

const InputFile = styled.input`
	display: none;
`;

interface StyledCustomAvatarProps {
	image: string;
	submitLogo: (files: FileList | null) => void;
	style?: React.CSSProperties;
	generateBgColorBy?: any;
	children?: any;
}

const StyledCustomAvatar: React.FC<StyledCustomAvatarProps> = ({
	image = '',
	submitLogo,
	style = {},
	generateBgColorBy,
	children,
}) => {
	const fileInputRef = React.useRef<HTMLInputElement>(null);

	const handleClick = () => {
		if (fileInputRef.current) {
			fileInputRef.current.click();
		}
	};

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		e.preventDefault();
		submitLogo(e?.target?.files);
	};

	const bgColor = useMemo(() => {
		return !image && generateBgColorBy ? generateAvatarColor(generateBgColorBy) : undefined;
	}, [generateBgColorBy, image]);

	return (
		<StyledAvatar
			bgColor={bgColor}
			style={{
				...style,
				backgroundImage: `url(${image})`,
				backgroundColor: bgColor ?? style?.backgroundColor,
				objectFit: 'contain',
			}}
			onClick={handleClick}
		>
			<ImageBackdrop className="MuiImageBackdrop-root">
				<CameraIcon style={{ color: '#fff' }} />
			</ImageBackdrop>
			<InputFile type="file" accept="image/*" ref={fileInputRef} onChange={handleFileChange} />
			{children}
		</StyledAvatar>
	);
};

export default StyledCustomAvatar;
